import React, { useState, useEffect, useRef } from 'react';
import { ArrowRight, Coffee, Wifi, Users, Clock, MapPin, Star } from 'lucide-react';

const CafeSection = () => {
  const [isVisible, setIsVisible] = useState(false);
  const [activeFeature, setActiveFeature] = useState(0);
  const sectionRef = useRef(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
        }
      },
      { threshold: 0.2 }
    );

    if (sectionRef.current) {
      observer.observe(sectionRef.current);
    }

    return () => observer.disconnect();
  }, []);

  // Auto-rotate features
  useEffect(() => {
    const interval = setInterval(() => {
      setActiveFeature(prev => (prev + 1) % features.length);
    }, 3000);

    return () => clearInterval(interval);
  }, []);

  const features = [
    {
      icon: Coffee,
      title: 'Café & Restauration',
      description: 'Menu varié et boissons de qualité',
      color: 'from-amber-500 to-orange-600'
    },
    {
      icon: Wifi,
      title: 'Espace de travail',
      description: 'WiFi gratuit et environnement calme',
      color: 'from-blue-500 to-indigo-600'
    },
    {
      icon: Users,
      title: 'Espace détente',
      description: 'Lieu convivial pour se relaxer',
      color: 'from-green-500 to-emerald-600'
    },
    {
      icon: Clock,
      title: 'Pendant votre service',
      description: 'Patientez confortablement',
      color: 'from-purple-500 to-violet-600'
    }
  ];

  const menuItems = [
    {
      category: 'Boissons chaudes',
      items: ['Café expresso', 'Cappuccino', 'Thé à la menthe', 'Chocolat chaud'],
      icon: '☕'
    },
    {
      category: 'Restauration',
      items: ['Sandwichs frais', 'Salades', 'Plats du jour', 'Pâtisseries'],
      icon: '🥪'
    },
    {
      category: 'Services',
      items: ['WiFi gratuit', 'Prises électriques', 'Journaux', 'Climatisation'],
      icon: '📶'
    }
  ];

  const openingHours = [
    { day: 'Lundi - Vendredi', hours: '8h00 - 18h00' },
    { day: 'Samedi', hours: '9h00 - 17h00' },
    { day: 'Dimanche', hours: 'Fermé' }
  ];

  return (
    <section 
      ref={sectionRef}
      className="py-20 bg-gradient-to-br from-amber-50 via-orange-50 to-red-50 relative overflow-hidden"
      itemScope
      itemType="https://schema.org/Restaurant"
    >
      {/* Background Elements */}
      <div className="absolute inset-0">
        <div className="absolute top-20 right-10 w-32 h-32 bg-amber-200/30 rounded-full animate-pulse" />
        <div className="absolute bottom-20 left-10 w-24 h-24 bg-orange-200/30 rounded-full animate-pulse" style={{ animationDelay: '1s' }} />
        
        {/* Coffee steam animation */}
        <div className="absolute inset-0 opacity-10">
          {[...Array(15)].map((_, i) => (
            <div
              key={i}
              className="absolute animate-steam text-3xl"
              style={{
                left: `${Math.random() * 100}%`,
                top: `${Math.random() * 100}%`,
                animationDelay: `${Math.random() * 5}s`,
                animationDuration: `${4 + Math.random() * 2}s`
              }}
            >
              {['☕', '🥐', '🍰', '🥪'][Math.floor(Math.random() * 4)]}
            </div>
          ))}
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        
        {/* Section Header */}
        <div className={`text-center mb-16 transition-all duration-1000 ${
          isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
        }`}>
          <div className="inline-flex items-center px-4 py-2 bg-amber-100 text-amber-800 rounded-full text-sm font-medium mb-4">
            <Coffee className="w-4 h-4 mr-2" />
            Nouveau concept à Bouskoura
          </div>
          
          <h2 className="text-4xl lg:text-5xl font-bold text-gray-900 mb-6">
            Café-Resto{' '}
            <span className="text-transparent bg-clip-text bg-gradient-to-r from-amber-600 to-orange-600">
              « L'ATELIER »
            </span>
          </h2>
          
          <p className="text-xl text-gray-600 max-w-3xl mx-auto" itemProp="description">
            Un café-restaurant chaleureux où nos clients peuvent se détendre, travailler 
            ou savourer des repas variés pendant l'entretien de leur véhicule.
          </p>
        </div>

        <div className="grid lg:grid-cols-2 gap-12 lg:gap-16 items-center">
          
          {/* Content Side */}
          <div className={`space-y-8 transition-all duration-1000 delay-300 ${
            isVisible ? 'opacity-100 translate-x-0' : 'opacity-0 -translate-x-8'
          }`}>
            
            {/* Main Features */}
            <div className="space-y-4">
              <h3 className="text-2xl font-bold text-gray-900 mb-6">Un espace unique</h3>
              
              <div className="grid gap-4">
                {features.map((feature, index) => {
                  const Icon = feature.icon;
                  const isActive = index === activeFeature;
                  
                  return (
                    <div
                      key={index}
                      className={`
                        group relative p-4 rounded-xl border-2 transition-all duration-500 cursor-pointer
                        ${isActive 
                          ? 'bg-gradient-to-r ' + feature.color + ' text-white border-transparent shadow-lg scale-105' 
                          : 'bg-white border-amber-200 hover:border-amber-300 hover:shadow-md'
                        }
                      `}
                      onMouseEnter={() => setActiveFeature(index)}
                    >
                      <div className="flex items-center space-x-4">
                        <div className={`
                          flex-shrink-0 w-12 h-12 rounded-xl flex items-center justify-center transition-transform duration-300
                          ${isActive ? 'bg-white/20 scale-110' : 'bg-amber-100'}
                        `}>
                          <Icon className={`w-6 h-6 ${isActive ? 'text-white' : 'text-amber-600'}`} />
                        </div>
                        
                        <div className="flex-1">
                          <h4 className={`text-lg font-bold ${isActive ? 'text-white' : 'text-gray-900'}`}>
                            {feature.title}
                          </h4>
                          <p className={`${isActive ? 'text-white/90' : 'text-gray-600'}`}>
                            {feature.description}
                          </p>
                        </div>
                      </div>
                      
                      {isActive && (
                        <div className="absolute -right-1 top-1/2 -translate-y-1/2 w-3 h-3 bg-white rounded-full animate-pulse" />
                      )}
                    </div>
                  );
                })}
              </div>
            </div>

            {/* Menu Preview */}
            <div className="space-y-4">
              <h4 className="text-xl font-bold text-gray-900">Notre offre</h4>
              <div className="grid gap-4">
                {menuItems.map((menu, index) => (
                  <div 
                    key={index}
                    className={`
                      p-4 bg-white rounded-xl shadow-sm border border-amber-100
                      transition-all duration-500 hover:shadow-md hover:scale-105
                      ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'}
                    `}
                    style={{ transitionDelay: `${(index + 4) * 200}ms` }}
                  >
                    <div className="flex items-start space-x-4">
                      <div className="text-3xl">{menu.icon}</div>
                      <div className="flex-1">
                        <h5 className="font-semibold text-gray-900 mb-2">{menu.category}</h5>
                        <div className="grid grid-cols-2 gap-1 text-sm text-gray-600">
                          {menu.items.map((item, idx) => (
                            <div key={idx} className="flex items-center">
                              <div className="w-1 h-1 bg-amber-500 rounded-full mr-2" />
                              {item}
                            </div>
                          ))}
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Opening Hours 
            <div className="space-y-4">
              <h4 className="text-xl font-bold text-gray-900">Horaires</h4>
              <div className="bg-white rounded-xl p-4 shadow-sm border border-amber-100">
                {openingHours.map((schedule, index) => (
                  <div key={index} className="flex justify-between items-center py-2 border-b border-amber-100 last:border-b-0">
                    <span className="font-medium text-gray-900">{schedule.day}</span>
                    <span className="text-gray-600">{schedule.hours}</span>
                  </div>
                ))}
              </div>
            </div>*/}

            {/* CTA */}

          </div>

          {/* Visual Side */}
          <div className={`relative transition-all duration-1000 delay-500 ${
            isVisible ? 'opacity-100 translate-x-0' : 'opacity-0 translate-x-8'
          }`}>
            
            {/* Main Restaurant Image */}
            <div className="relative">
              <div className="relative bg-gradient-to-br from-amber-100 to-orange-100 rounded-3xl overflow-hidden shadow-2xl">
                <img
                  src="/images/restaurant.png"
                  alt="Intérieur du café-restaurant L'Atelier"
                  className="w-full h-96 object-cover"
                  loading="lazy"
                  itemProp="image"
                />
                
                {/* Overlay with ambiance */}
                <div className="absolute inset-0 bg-gradient-to-t from-black/40 via-transparent to-transparent" />
                
                <div className="absolute bottom-0 left-0 right-0 p-6 text-white">
                  <h3 className="text-2xl font-bold mb-4">L'ATELIER Café-Resto</h3>
                  <div className="grid grid-cols-3 gap-4">
                    <div className="text-center">
                      <div className="text-2xl font-bold">50+</div>
                      <div className="text-sm opacity-90">Places assises</div>
                    </div>
                    <div className="text-center">
                      <div className="text-2xl font-bold">WiFi</div>
                      <div className="text-sm opacity-90">Gratuit</div>
                    </div>
                    <div className="text-center">
                      <div className="text-2xl font-bold">A/C</div>
                      <div className="text-sm opacity-90">Climatisé</div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Floating Features */}
              <div className="absolute -top-4 -left-4 bg-white rounded-xl p-4 shadow-lg border animate-float">
                <div className="text-center">
                  <div className="text-lg font-bold text-amber-600">Nouveau</div>
                  <div className="text-xs text-gray-600">Concept unique</div>
                </div>
              </div>

              <div className="absolute -bottom-4 -right-4 bg-white rounded-xl p-4 shadow-lg border animate-float-delayed">
                <div className="text-center">
                  <div className="text-lg font-bold text-green-600">Bio</div>
                  <div className="text-xs text-gray-600">Produits frais</div>
                </div>
              </div>

              <div className="absolute top-1/4 -right-6 bg-white rounded-xl p-3 shadow-lg border animate-float">
                <div className="text-center">
                  <div className="text-lg font-bold text-blue-600">Work</div>
                  <div className="text-xs text-gray-600">Friendly</div>
                </div>
              </div>
            </div>

            {/* Amenities Grid */}
            <div className="mt-8 grid grid-cols-2 gap-4">
              <div className="bg-white p-4 rounded-xl shadow-sm border border-amber-100">
                <div className="text-center">
                  <div className="text-3xl mb-2">☕</div>
                  <h4 className="font-semibold text-gray-900">Café premium</h4>
                  <p className="text-sm text-gray-600">Sélection de grains</p>
                </div>
              </div>
              <div className="bg-white p-4 rounded-xl shadow-sm border border-amber-100">
                <div className="text-center">
                  <div className="text-3xl mb-2">🥪</div>
                  <h4 className="font-semibold text-gray-900">Cuisine fraîche</h4>
                  <p className="text-sm text-gray-600">Préparée sur place</p>
                </div>
              </div>
            </div>

            {/* Virtual Menu Teaser 
            <div className="mt-6 bg-white rounded-2xl p-6 shadow-lg border border-amber-100">
              <h4 className="font-bold text-gray-900 mb-4 text-center">Menu du jour</h4>
              <div className="space-y-3">
                <div className="flex justify-between items-center">
                  <span className="text-gray-700">Café Expresso</span>
                  <span className="font-semibold text-amber-600">15 DH</span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-gray-700">Sandwich Club</span>
                  <span className="font-semibold text-amber-600">45 DH</span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-gray-700">Salade César</span>
                  <span className="font-semibold text-amber-600">35 DH</span>
                </div>
                <div className="border-t border-amber-100 pt-2 text-center">
                  <span className="text-sm text-gray-500">+ de choix sur place</span>
                </div>
              </div>
            </div>*/}
          </div>
        </div>
      </div>
            <div className={`transition-all duration-1000  flex justify-center mt-8 delay-700 ${
              isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'
            }`}>
              <a
                href="https://goo.gl/maps/example"
                target="_blank"
                rel="noopener noreferrer"
                className="group inline-flex items-center 
                scale-125
                justify-center w-auto sm:w-auto px-8 py-4   bg-gradient-to-r from-amber-600 to-orange-600 text-white font-bold 
                rounded-xl transition-all duration-300 transform hover:scale-150 hover:shadow-xl"
                itemProp="url"
              >
                <MapPin className="w-5 h-5 mr-3" />
                <span>Visiter L'ATELIER</span>
                <ArrowRight className="ml-3 w-5 h-5 transition-transform group-hover:translate-x-1" />
              </a>
            </div>
      {/* Structured Data */}
      <script 
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "Restaurant",
            "name": "Café-Resto L'Atelier",
            "description": "Un café-restaurant chaleureux où nos clients peuvent se détendre, travailler ou savourer des repas variés",
            "url": "https://faucon-bleu.ma",
            "address": {
              "@type": "PostalAddress",
              "addressLocality": "Bouskoura",
              "addressRegion": "Casablanca",
              "addressCountry": "MA"
            },
            "servesCuisine": "Cuisine variée",
            "priceRange": "$$",
            "openingHoursSpecification": [
              {
                "@type": "OpeningHoursSpecification",
                "dayOfWeek": ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday"],
                "opens": "08:00",
                "closes": "18:00"
              },
              {
                "@type": "OpeningHoursSpecification",
                "dayOfWeek": ["Saturday"],
                "opens": "09:00",
                "closes": "17:00"
              }
            ],
            "amenityFeature": [
              {
                "@type": "LocationFeatureSpecification",
                "name": "Wi-Fi",
                "value": true
              },
              {
                "@type": "LocationFeatureSpecification",
                "name": "Espace de travail",
                "value": true
              },
              {
                "@type": "LocationFeatureSpecification",
                "name": "Climatisation",
                "value": true
              }
            ]
          })
        }}
      />

      <style jsx>{`
        @keyframes float {
          0%, 100% { transform: translateY(0px) rotate(0deg); }
          33% { transform: translateY(-8px) rotate(2deg); }
          66% { transform: translateY(-4px) rotate(-2deg); }
        }
        
        @keyframes float-delayed {
          0%, 100% { transform: translateY(0px) rotate(0deg); }
          50% { transform: translateY(-6px) rotate(1deg); }
        }
        
        @keyframes steam {
          0%, 100% { transform: translateY(0px) scale(1); opacity: 0.1; }
          50% { transform: translateY(-20px) scale(1.2); opacity: 0.3; }
        }
        
        .animate-float {
          animation: float 3s ease-in-out infinite;
        }
        
        .animate-float-delayed {
          animation: float-delayed 3s ease-in-out infinite 1.5s;
        }
        
        .animate-steam {
          animation: steam 4s ease-in-out infinite;
        }
      `}</style>
    </section>
  );
};

export default CafeSection;